import React from 'react';
import { ArrowUpRight, MessageCircle, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { EMAIL, LOCATION, PHONE_DISPLAY, WHATSAPP_URL } from '../constants';
import { Reveal } from './ui-blocks';

const ContactRow = ({ icon: Icon, label, value, href, testId }) => {
  const Tag = href ? 'a' : 'div';
  const linkProps = href ? { href, target: href.startsWith('http') ? '_blank' : undefined, rel: 'noopener noreferrer' } : {};
  return (
    <Tag
      {...linkProps}
      data-testid={testId}
      className={`group flex items-center justify-between gap-6 py-6 border-t border-cream/15 ${href ? 'hover:pl-3 transition-[padding] duration-300' : ''}`}
    >
      <div className="flex items-center gap-5">
        <span className="w-10 h-10 flex items-center justify-center border border-cream/20 text-gold shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-cream/50">{label}</p>
          <p className="mt-1 font-heading text-xl md:text-2xl text-cream">{value}</p>
        </div>
      </div>
      {href && <ArrowUpRight className="w-5 h-5 text-gold transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />}
    </Tag>
  );
};

const Contact = () => {
  const { t } = useLanguage();

  return (
    <section id="contact" data-testid="contact-section" className="noise relative bg-bordeaux text-cream pt-24 md:pt-32 pb-20 overflow-hidden">
      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16">
        <div className="lg:col-span-7">
          <Reveal>
            <span className="eyebrow text-gold">{t.contact.eyebrow}</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 data-testid="contact-headline" className="mt-7 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-2xl">
              {t.contact.headline}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl text-base md:text-lg text-cream/70 leading-relaxed">{t.contact.subtitle}</p>
          </Reveal>

          <Reveal delay={0.3} className="mt-14 border-b border-cream/15">
            <ContactRow icon={MessageCircle} label={t.contact.phone} value={PHONE_DISPLAY} href={WHATSAPP_URL} testId="contact-whatsapp-link" />
            <ContactRow icon={Mail} label={t.contact.email} value={EMAIL} href={`mailto:${EMAIL}`} testId="contact-email-link" />
            <ContactRow icon={MapPin} label={t.contact.location} value={LOCATION} testId="contact-location" />
          </Reveal>
        </div>

        <div className="lg:col-span-5 flex">
          <Reveal delay={0.25} className="w-full lg:mt-24">
            <div className="h-full bg-gold text-ink p-10 md:p-12 flex flex-col justify-between gap-10" data-testid="contact-card">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-bordeaux">{t.contact.whatsapp}</span>
                <h3 className="mt-5 font-heading text-3xl md:text-4xl leading-tight">{t.contact.cardTitle}</h3>
                <p className="mt-5 text-ink/70 leading-relaxed">{t.contact.cardText}</p>
              </div>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="group inline-flex items-center justify-between gap-4 bg-ink hover:bg-bordeaux text-cream font-bold px-7 py-5"
              >
                <span className="inline-flex items-center gap-3">
                  <MessageCircle className="w-5 h-5" strokeWidth={2} />
                  {t.contact.whatsapp}
                </span>
                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
